import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Analytics } from '@vercel/analytics/react';
import GitHubAuth from './GitHubAuth';
import CommitForm from './CommitForm';

function getTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

function getErrorFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('error');
}

export default function App() {
  const [token, setToken] = useState(null);
  const [owner, setOwner] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const errorMsg = getErrorFromUrl();
    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    const t = getTokenFromUrl();
    if (t) {
      setToken(t);
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchUsername(t);
    }
  }, []);

  const fetchUsername = async (token) => {
    setLoading(true);
    setError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const res = await axios.get(`${apiUrl}/api/user?token=${token}`);
      setOwner(res.data.username);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.details || err.message || 'Unknown error';
      setError(`Failed to fetch GitHub username: ${errorMsg}. Please enter it manually.`);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <GitHubAuth />
        {error && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: '#ef4444',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            maxWidth: '400px',
            zIndex: 1000,
            animation: 'fadeIn 0.3s ease-out',
          }}>
            <strong>Authentication Error:</strong> {error}
          </div>
        )}
        <Analytics />
      </>
    );
  }

  return (
    <>
      <div style={styles.container}>
        <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <svg style={styles.githubLogo} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" fill="#10a37f"/>
            </svg>
          </div>
          <h2 style={styles.title}>GitGreen</h2>
          <p style={styles.subtitle}>Make your GitHub profile glow green</p>
        </div>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}>⏳</div>
            <p style={styles.loadingText}>Fetching your GitHub information...</p>
          </div>
        ) : !owner ? (
          <form 
            onSubmit={(e) => { 
              e.preventDefault(); 
              if (owner.trim()) setOwner(owner.trim()); 
            }}
            style={styles.form}
          >
            {error && (
              <div style={styles.errorBox}>
                {error}
              </div>
            )}
            <div style={styles.inputGroup}>
              <label style={styles.label}>GitHub Username</label>
              <input 
                value={owner} 
                onChange={e => setOwner(e.target.value)} 
                placeholder="Enter your GitHub username" 
                required 
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.borderColor = '#10a37f';
                  e.target.style.background = '#4a4b5c';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#565869';
                  e.target.style.background = '#40414f';
                }}
                autoFocus
              />
            </div>
            <button 
              type="submit" 
              style={styles.submitButton}
              onMouseEnter={(e) => {
                e.target.style.background = '#0d8f6e';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(16, 163, 127, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#10a37f';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 163, 127, 0.3)';
              }}
            >
              Continue →
            </button>
          </form>
        ) : (
          <CommitForm token={token} owner={owner} />
        )}
      </div>
    </div>
    <Analytics />
    </>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#212121',
    padding: '20px',
  },
  card: {
    background: '#343541',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    maxWidth: '600px',
    width: '100%',
    border: '1px solid #565869',
    animation: 'fadeIn 0.5s ease-out',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
    animation: 'fadeIn 0.6s ease-out',
  },
  iconContainer: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'center',
  },
  githubLogo: {
    width: '56px',
    height: '56px',
    filter: 'drop-shadow(0 4px 12px rgba(16, 163, 127, 0.4))',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#ececf1',
    margin: '0 0 8px 0',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#8e8ea0',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    animation: 'fadeIn 0.7s ease-out',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#ececf1',
    letterSpacing: '0.2px',
  },
  input: {
    padding: '12px 16px',
    fontSize: '15px',
    background: '#40414f',
    border: '1px solid #565869',
    borderRadius: '10px',
    outline: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#ececf1',
    fontFamily: 'inherit',
  },
  submitButton: {
    background: '#10a37f',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 24px',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 12px rgba(16, 163, 127, 0.3)',
    letterSpacing: '0.2px',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  spinner: {
    fontSize: '48px',
    marginBottom: '16px',
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#8e8ea0',
    fontSize: '15px',
    animation: 'pulse 2s ease-in-out infinite',
  },
  errorBox: {
    padding: '12px 16px',
    background: '#5c2d2d',
    color: '#fca5a5',
    border: '1px solid #7f1d1d',
    borderRadius: '10px',
    marginBottom: '16px',
    fontSize: '14px',
    animation: 'slideIn 0.3s ease-out',
  },
};
